import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BrainCircuit, CheckCircle2, ChevronLeft, Clock, GitBranch, Network, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type SnapshotEntity = {
  id: string;
  canonical_name?: string | null;
  entity_type?: string | null;
  salience?: number | null;
  aliases?: string[] | null;
  last_seen_at?: string | null;
};

type SnapshotEdge = {
  id: string;
  source_entity_id?: string | null;
  target_entity_id?: string | null;
  source_label?: string | null;
  target_label?: string | null;
  relation_type?: string | null;
  weight?: number | null;
  evidence_count?: number | null;
  last_seen_at?: string | null;
};

type MentalMapSnapshot = {
  org_id: string;
  group_id: string;
  turn_id: string | null;
  generated_at: string;
  pipeline_run: Record<string, unknown> | null;
  entities: SnapshotEntity[];
  edges: SnapshotEdge[];
  counts: { entities: number; edges: number };
};

function formatNumber(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return value.toFixed(3);
}

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function readObject(value: unknown) {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function readString(value: unknown, fallback = "—") {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function readNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function buildDecisionPath(contextSnapshot: Record<string, unknown> | null, decisionTrace: Record<string, unknown> | null) {
  const context = readObject(contextSnapshot?.context);
  const cortex = readObject(contextSnapshot?.cortex);
  const user = readObject(contextSnapshot?.user);
  const latency = readNumber(decisionTrace?.latency_ms);
  const inhibitor = readString(decisionTrace?.inhibitor_action, "send");
  const status = readString(decisionTrace?.status, contextSnapshot ? "success" : "—");

  return [
    {
      label: "Entrada",
      value: readString(user?.sender_name, readString(user?.sender_jid, "mensagem recebida")),
      detail: readString(contextSnapshot?.message_id, readString(contextSnapshot?.turn_id)),
      tone: contextSnapshot ? "ok" : "idle",
    },
    {
      label: "Contexto",
      value: `${readNumber(context?.recent_messages_count) ?? 0} msgs · ${readNumber(context?.attachments_count) ?? 0} anexos`,
      detail: readString(context?.typology, "perfil sem tipologia"),
      tone: context ? "ok" : "idle",
    },
    {
      label: "Cortex",
      value: readString(cortex?.group_state, "estado ausente"),
      detail: readString(cortex?.threat_detected ? cortex?.threat_type : null, "sem ameaça ativa"),
      tone: cortex?.threat_detected ? "warn" : cortex ? "ok" : "idle",
    },
    {
      label: "Skill",
      value: readString(decisionTrace?.skill_used, "sem skill"),
      detail: readString(decisionTrace?.pattern_sep_action, "pattern separation neutro"),
      tone: decisionTrace ? "ok" : "idle",
    },
    {
      label: "Modelo",
      value: readString(decisionTrace?.model_used, "modelo ausente"),
      detail: latency === null ? "latência não registrada" : `${latency.toLocaleString("pt-BR")} ms`,
      tone: decisionTrace?.model_used ? "ok" : "idle",
    },
    {
      label: "Gate final",
      value: inhibitor,
      detail: status,
      tone: inhibitor === "send" ? "ok" : inhibitor === "—" ? "idle" : "warn",
    },
  ] as const;
}

export default function GroupMentalMap() {
  const { groupId } = useParams<{ groupId: string }>();
  const { data: orgId } = useCurrentOrgId();
  const [turnId, setTurnId] = useState("");

  const { data: group } = useQuery({
    queryKey: ["group", groupId],
    queryFn: async () => {
      if (!groupId) return null;
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name, member_count")
        .eq("id", groupId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!groupId,
  });

  const snapshotQuery = useQuery({
    queryKey: ["mental-map-snapshot", orgId, groupId, turnId.trim() || "latest"],
    queryFn: async (): Promise<MentalMapSnapshot> => {
      if (!orgId || !groupId) throw new Error("Organização ou grupo ausente");
      const { data, error } = await supabase.functions.invoke("mental-map-snapshot", {
        body: {
          org_id: orgId,
          group_id: groupId,
          turn_id: turnId.trim() || undefined,
        },
      });
      if (error) throw error;
      if (!data) throw new Error("Snapshot vazio");
      return data as MentalMapSnapshot;
    },
    enabled: !!orgId && !!groupId,
  });

  const pipelineRun = snapshotQuery.data?.pipeline_run ?? null;
  const contextSnapshot = useMemo(() => readObject(pipelineRun?.context_snapshot), [pipelineRun]);
  const decisionTrace = useMemo(() => readObject(pipelineRun?.decision_trace), [pipelineRun]);
  const decisionPath = useMemo(() => buildDecisionPath(contextSnapshot, decisionTrace), [contextSnapshot, decisionTrace]);

  if (!groupId) {
    return <p className="p-6 text-sm text-destructive">Grupo não informado.</p>;
  }

  return (
    <div className="container max-w-7xl py-6 space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2">
          <Link to="/groups">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Voltar para grupos
          </Link>
        </Button>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center gap-3">
            <Network className="h-7 w-7 text-primary" />
            <div>
              <h1 className="text-2xl font-semibold">Mapa mental do grupo</h1>
              <p className="text-sm text-muted-foreground">
                {group?.name ?? "Grupo"} — entidades, relações e snapshot do turno.
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
            <div className="relative sm:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={turnId}
                onChange={(event) => setTurnId(event.target.value)}
                placeholder="turn_id opcional"
                className="pl-9"
              />
            </div>
            <Button variant="outline" onClick={() => snapshotQuery.refetch()} disabled={snapshotQuery.isFetching}>
              <RefreshCw className={`h-4 w-4 mr-2 ${snapshotQuery.isFetching ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      {snapshotQuery.error ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="pt-6 text-sm text-destructive">
            {(snapshotQuery.error as Error).message}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Entidades" value={snapshotQuery.data?.counts.entities ?? 0} />
        <MetricCard label="Relações" value={snapshotQuery.data?.counts.edges ?? 0} />
        <MetricCard label="Membros" value={group?.member_count ?? 0} />
        <MetricCard label="Turno" value={snapshotQuery.data?.turn_id ? "Filtrado" : "Atual"} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><BrainCircuit className="h-4 w-4" /> Caminho de decisão</CardTitle>
          <CardDescription>Leitura condensada do contexto, roteamento e gate final persistidos no turno.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            {decisionPath.map((step, index) => (
              <DecisionStep key={step.label} index={index + 1} {...step} />
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Entidades principais</CardTitle>
            <CardDescription>Ordenadas pelo snapshot read-only do mapa mental.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Salience</TableHead>
                  <TableHead>Última vez</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(snapshotQuery.data?.entities ?? []).map((entity) => (
                  <TableRow key={entity.id}>
                    <TableCell className="font-medium">
                      <div className="space-y-1">
                        <span>{entity.canonical_name ?? entity.id.slice(0, 8)}</span>
                        {entity.aliases?.length ? (
                          <p className="text-xs text-muted-foreground truncate max-w-72">{entity.aliases.join(", ")}</p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{entity.entity_type ?? "entidade"}</Badge></TableCell>
                    <TableCell>{formatNumber(entity.salience)}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(entity.last_seen_at)}</TableCell>
                  </TableRow>
                ))}
                {!snapshotQuery.isLoading && !snapshotQuery.data?.entities.length ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Nenhuma entidade encontrada.</TableCell></TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Snapshot do turno</CardTitle>
            <CardDescription>Contexto persistido e trilha de decisão em pipeline_runs.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <JsonBlock title="context_snapshot" value={contextSnapshot} />
            <JsonBlock title="decision_trace" value={decisionTrace} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><GitBranch className="h-4 w-4" /> Relações</CardTitle>
          <CardDescription>Arestas resolvidas com labels das entidades de origem e destino.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Origem</TableHead>
                <TableHead>Relação</TableHead>
                <TableHead>Destino</TableHead>
                <TableHead>Peso</TableHead>
                <TableHead>Evidências</TableHead>
                <TableHead>Última vez</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(snapshotQuery.data?.edges ?? []).map((edge) => (
                <TableRow key={edge.id}>
                  <TableCell className="font-medium">{edge.source_label ?? edge.source_entity_id?.slice(0, 8) ?? "—"}</TableCell>
                  <TableCell><Badge variant="secondary">{edge.relation_type ?? "relacionado"}</Badge></TableCell>
                  <TableCell className="font-medium">{edge.target_label ?? edge.target_entity_id?.slice(0, 8) ?? "—"}</TableCell>
                  <TableCell>{formatNumber(edge.weight)}</TableCell>
                  <TableCell>{edge.evidence_count ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(edge.last_seen_at)}</TableCell>
                </TableRow>
              ))}
              {!snapshotQuery.isLoading && !snapshotQuery.data?.edges.length ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma relação encontrada.</TableCell></TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold text-foreground">{typeof value === "number" ? value.toLocaleString("pt-BR") : value}</p>
      </CardContent>
    </Card>
  );
}

function DecisionStep({
  index,
  label,
  value,
  detail,
  tone,
}: {
  index: number;
  label: string;
  value: string;
  detail: string;
  tone: "ok" | "warn" | "idle";
}) {
  const Icon = tone === "warn" ? ShieldCheck : tone === "ok" ? CheckCircle2 : Clock;
  const toneClass = tone === "warn"
    ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
    : tone === "ok"
      ? "border-primary/30 bg-primary/10 text-primary"
      : "border-border bg-muted/30 text-muted-foreground";

  return (
    <div className="relative rounded-md border bg-card p-3">
      {index > 1 ? <div className="absolute -left-3 top-7 hidden h-px w-3 bg-border xl:block" /> : null}
      <div className="flex items-start gap-3">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${toneClass}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium text-muted-foreground">{index.toString().padStart(2, "0")}</span>
            <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
          </div>
          <p className="truncate text-sm font-medium text-foreground" title={value}>{value}</p>
          <p className="line-clamp-2 text-xs text-muted-foreground" title={detail}>{detail}</p>
        </div>
      </div>
    </div>
  );
}

function JsonBlock({ title, value }: { title: string; value: Record<string, unknown> | null }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
        <Badge variant={value ? "default" : "outline"}>{value ? "presente" : "vazio"}</Badge>
      </div>
      <pre className="max-h-72 overflow-auto rounded-md border bg-muted/30 p-3 text-xs text-foreground">
        {value ? JSON.stringify(value, null, 2) : "null"}
      </pre>
    </div>
  );
}